<template>
  <div
    class="portfolio-card mb-2"
    :class="`portfolio-card--${portfolio.portfolio_type.toLowerCase()}`"
  >
    <div class="card-body px-3 py-2">
      <div class="d-flex align-items-center">
        <!-- Expand Toggle -->
        <button
          class="btn btn-link text-dark p-0 me-3"
          :aria-expanded="isExpanded"
          :aria-label="
            $t('portfolios.show_items', {
              count: portfolio.items.length,
            })
          "
          @click="$emit('toggle')"
        >
          <FontAwesomeIcon
            icon="fas fa-chevron-down"
            class="card-chevron"
            :class="{ 'card-chevron--collapsed': !isExpanded }"
          />
        </button>

        <!-- Icon + Name & Edit -->
        <div class="d-flex align-items-center gap-2 flex-grow-1 me-4">
          <FontAwesomeIcon :icon="config.icon" :class="config.iconColorClass" />
          <div v-if="!isEditing" class="d-flex align-items-center gap-2">
            <span class="fw-semibold text-dark">
              {{ displayName }}
            </span>
            <span
              class="badge rounded-pill"
              :class="config.badgeClass"
              :title="badgeTooltip"
            >
              {{ badgeText }}
            </span>
            <span
              v-if="portfolio.is_default"
              class="badge rounded-pill bg-secondary-subtle text-secondary-emphasis"
              :title="$t('portfolios.tooltip_default')"
            >
              {{ $t("portfolios.badge_default") }}
            </span>
            <button
              v-if="config.renameable"
              class="btn btn-link text-secondary p-0 ms-1"
              :aria-label="$t('common.edit')"
              @click="startRename"
            >
              <FontAwesomeIcon icon="fas fa-pencil-alt" size="sm" />
            </button>
          </div>
          <div
            v-else
            class="d-flex align-items-center gap-2 w-100"
            style="max-width: 400px"
          >
            <input
              v-model="editingName"
              type="text"
              class="form-control form-control-sm"
              @keyup.enter="handleRename"
              @keyup.esc="cancelRename"
              autofocus
              :disabled="isProcessing"
            />
            <button
              class="btn btn-sm btn-primary"
              :aria-label="$t('common.save')"
              @click="handleRename"
              :disabled="isProcessing"
            >
              <FontAwesomeIcon v-if="!isProcessing" icon="fas fa-check" />
              <span
                v-else
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
            </button>
            <button
              class="btn btn-sm btn-outline-secondary"
              :aria-label="$t('common.cancel')"
              @click="cancelRename"
              :disabled="isProcessing"
            >
              <FontAwesomeIcon icon="fas fa-times" />
            </button>
          </div>
        </div>

        <!-- Volume -->
        <div class="d-none d-md-flex align-items-center me-4">
          <span
            class="fw-bold text-nowrap"
            :class="portfolio.total_volume > 0 ? '' : 'text-muted'"
          >
            {{ formatNumberWithSpaces(portfolio.total_volume || 0) }} MWh
          </span>
        </div>

        <!-- Actions -->
        <div class="d-flex gap-2">
          <template v-if="isMergeable || config.splittable">
            <div class="dropdown">
              <button
                class="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                :title="$t('common.actions.title')"
                :aria-label="$t('common.actions.title')"
              >
                <FontAwesomeIcon icon="fas fa-ellipsis-v" />
              </button>
              <ul class="dropdown-menu dropdown-menu-end">
                <li v-if="isMergeable">
                  <button
                    class="dropdown-item d-flex align-items-center gap-2"
                    @click="$emit('merge', portfolio)"
                  >
                    <FontAwesomeIcon
                      icon="fas fa-compress-alt"
                      class="text-muted"
                    />
                    {{ $t("portfolios.merge") }}
                  </button>
                </li>
                <li v-if="config.splittable">
                  <button
                    class="dropdown-item d-flex align-items-center gap-2"
                    @click="$emit('split', portfolio)"
                  >
                    <FontAwesomeIcon
                      icon="fas fa-code-branch"
                      class="text-muted"
                    />
                    {{ $t("portfolios.split") }}
                  </button>
                </li>
              </ul>
            </div>
          </template>

          <template v-if="config.moveable">
            <button
              class="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
              :disabled="!portfolio.total_volume"
              :aria-label="$t('portfolios.move_items')"
              @click="$emit('move', portfolio)"
            >
              <FontAwesomeIcon icon="fas fa-exchange-alt" />
              <span class="d-none d-sm-inline">
                {{ $t("portfolios.move_items") }}
              </span>
            </button>
          </template>

          <template v-if="config.linkLabel">
            <button
              class="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
              :aria-label="$t(config.linkLabel)"
              @click="$emit('navigate', portfolio)"
            >
              <span class="d-none d-sm-inline">
                {{ $t(config.linkLabel) }}
              </span>
              <FontAwesomeIcon icon="fas fa-arrow-right" />
            </button>
          </template>
        </div>
      </div>

      <!-- Expanded Items -->
      <div class="card-expand" :class="{ 'card-expand--open': isExpanded }">
        <div class="card-expand__inner">
          <div v-if="isExpanded" class="mt-3 border-top pt-3">
            <div
              v-if="portfolio.items.length === 0"
              class="text-center text-muted py-3"
            >
              {{ $t("portfolios.empty_portfolio") }}
            </div>
            <PortfolioItemsTable
              v-else
              :items="portfolio.items"
              :disable-selection="true"
              :default-visible-columns="PORTFOLIO_CARD_COLUMNS"
              :show-column-selector="false"
              storage-context="virtual"
              table-size="compact"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { formatMoney, formatNumberWithSpaces } from "@/composables/formatUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import PortfolioItemsTable from "@/dashboard/inventory_management/common/PortfolioItemsTable.vue";
import { PORTFOLIO_CARD_COLUMNS } from "@/dashboard/inventory_management/common/portfolioTableColumns";
import { PORTFOLIO_TYPE_CONFIG } from "@/dashboard/inventory_management/portfolios/portfolioConfig";
import type { PortfolioGroup, PortfolioType } from "../types";
import { useProductionStrategyStore } from "@/dashboard/production_strategy/useProductionStrategyStore";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  portfolio: PortfolioGroup;
  isExpanded: boolean;
}>();

const emit = defineEmits<{
  toggle: [];
  merge: [portfolio: PortfolioGroup];
  split: [portfolio: PortfolioGroup];
  move: [portfolio: PortfolioGroup];
  navigate: [portfolio: PortfolioGroup];
  refresh: [];
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const productionStrategyStore = useProductionStrategyStore();

const isEditing = ref(false);
const editingName = ref("");
const isProcessing = ref(false);

const config = computed(() => {
  const matched = PORTFOLIO_TYPE_CONFIG[props.portfolio.portfolio_type];
  if (!matched) {
    console.warn(
      `Unknown portfolio type "${props.portfolio.portfolio_type}" — rendering with no actions`
    );
    return {
      icon: "fas fa-question-circle",
      iconColorClass: "text-muted",
      borderClass: "border-secondary",
      section: "inventory" as const,
      badgeClass: "bg-secondary-subtle text-secondary-emphasis",
      renameable: false,
      splittable: false,
      mergeable: false,
      moveable: false,
      allowedMoveTargets: [] as PortfolioType[],
    };
  }
  return matched;
});

const isMergeable = computed(
  () => config.value.mergeable && !props.portfolio.is_default
);

const configuredLimitPrice = computed(() => {
  if (props.portfolio.portfolio_type !== "SPOT_MIN_PRICE") return null;
  const storePortfolio = productionStrategyStore.portfolios.find(
    (p) => p.id === props.portfolio.id
  );
  return (
    props.portfolio.limit_price ??
    storePortfolio?.portfolio_instructions?.limit_price ??
    null
  );
});

const displayName = computed(() =>
  props.portfolio.portfolio_type === "OWN_USE"
    ? t("charts.values.portfolio_type.OWN_USE")
    : props.portfolio.name
);

const badgeText = computed(() => {
  const type = props.portfolio.portfolio_type;
  if (type === "HOLD") return t("portfolios.badge_own_registry");
  if (type === "VIRTUAL") return t("portfolios.badge_soldera_account");
  if (type === "SPOT_MARKET_PRICE") return t("portfolios.badge_spot_market");
  if (type === "SPOT_MIN_PRICE" && configuredLimitPrice.value) {
    return t("portfolios.badge_spot_min", {
      price: formatMoney(Number(configuredLimitPrice.value)),
    });
  }
  if (type === "SPOT_MIN_PRICE") return t("portfolios.badge_spot_min_default");
  if (type === "SPOT_STX") return t("portfolios.badge_stx");
  if (type === "PPA") return t("portfolios.badge_ppa");
  if (type === "FORWARD") return t("portfolios.badge_forward");
  if (type === "OWN_USE") return t("portfolios.badge_own_use");
  return type;
});

const badgeTooltip = computed(() => {
  const type = props.portfolio.portfolio_type;
  if (type === "HOLD") return t("portfolios.tooltip_own_registry");
  if (type === "VIRTUAL") return t("portfolios.tooltip_soldera_account");
  if (type === "SPOT_MARKET_PRICE") return t("portfolios.tooltip_spot_market");
  if (type === "SPOT_MIN_PRICE") return t("portfolios.tooltip_spot_min");
  if (type === "OWN_USE") return t("portfolios.tooltip_own_use");
  return "";
});

const startRename = () => {
  editingName.value = props.portfolio.name;
  isEditing.value = true;
};

const cancelRename = () => {
  isEditing.value = false;
  editingName.value = "";
};

const handleRename = async () => {
  if (!editingName.value.trim() || editingName.value === props.portfolio.name) {
    cancelRename();
    return;
  }

  isProcessing.value = true;
  try {
    await goSolidApi.patch(`/api/portfolio/${props.portfolio.id}/rename`, {
      name: editingName.value.trim(),
    });
    globalSuccessHandler(t("common.success"), t("common.saved_successfully"));
    cancelRename();
    emit("refresh");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isProcessing.value = false;
  }
};
</script>

<style lang="scss">
@use "@/dashboard/inventory_management/portfolios/portfolio-colors";
</style>

<style scoped lang="scss">
$ease-out: cubic-bezier(0.25, 0, 0.3, 1);

.portfolio-card {
  position: relative;
  background: var(--phoenix-card-bg, #fff);
  border: 1px solid var(--phoenix-border-color, #d8d2bf);
  border-radius: var(--phoenix-border-radius, 0.375rem);
  transition: box-shadow 120ms $ease-out;

  &:hover {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  }
}

.portfolio-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  border-radius: var(--phoenix-border-radius, 0.375rem) 0 0
    var(--phoenix-border-radius, 0.375rem);
}

.portfolio-card--hold::before,
.portfolio-card--virtual::before {
  background: #51524c;
}

.portfolio-card--own_use::before {
  background: #8d948f;
}

.portfolio-card--spot_market_price::before,
.portfolio-card--spot_stx::before {
  background: #8b5cf6;
}

.portfolio-card--spot_min_price::before {
  background: #8b5cf6;
}

.portfolio-card--ppa::before {
  background: rgba(var(--phoenix-success-rgb, 25, 135, 84), 1);
}

.portfolio-card--forward::before {
  background: rgba(var(--phoenix-primary-rgb, 13, 110, 253), 1);
}

.card-chevron {
  transition: transform 120ms $ease-out;
}

.card-chevron--collapsed {
  transform: rotate(-90deg);
}

.card-expand {
  display: grid;
  grid-template-rows: 1fr;
  opacity: 1;
  transition:
    grid-template-rows 150ms $ease-out,
    opacity 100ms $ease-out;
}

.card-expand:not(.card-expand--open) {
  grid-template-rows: 0fr;
  opacity: 0;
}

.card-expand__inner {
  overflow: hidden;
}

@media (prefers-reduced-motion: reduce) {
  .card-chevron,
  .card-expand,
  .portfolio-card {
    transition: none;
  }
}
</style>
